import * as XLSX from 'xlsx';
import { BudgetFolder, BudgetItem } from '../types';

// Headers for the Excel template
const HEADERS = ['Nivel', 'Descripción', 'Unidad', 'Cantidad', 'Precio Unitario (Ref)', 'ID'];

/**
 * Generates and downloads an Excel file reflecting the current Budget state.
 * Includes IDs to allow updates/preservation of APU connections.
 */
export const exportBudgetToExcel = (tree: BudgetFolder[], items: BudgetItem[]) => {
    const rows: any[][] = [HEADERS];

    // Recursive function to process folders and their items
    const processFolder = (folder: BudgetFolder, currentLevel: string) => {
        // Add Folder Row
        rows.push([
            currentLevel,
            folder.name,
            '', // Unit
            '', // Qty
            '', // Price Unitario (Ref) - Folders don't have these
            folder.id // ID
        ]);

        // Filter items belonging to this folder
        const folderItems = items.filter(item => item.folderId === folder.id);

        // Sort items by their code for consistent export order
        folderItems.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' }));

        // Add Items belonging to this folder
        folderItems.forEach(item => {
            rows.push([
                item.code, // Nivel (WBS code for item)
                item.description,
                item.unit,
                item.quantity,
                item.unitPrice, // Export actual unit price for reference
                item.id // ID
            ]);
        });

        // Sort child folders by name for consistent export order
        folder.children.sort((a, b) => a.name.localeCompare(b.name));

        // Recursively process child folders
        folder.children.forEach((childFolder, index) => {
            // Synthesize child level based on parent level and index
            const childLevel = `${currentLevel}.${index + 1} `;
            processFolder(childFolder, childLevel);
        });
    };

    // Process root folders
    // Sort root folders by name for consistent export order
    tree.sort((a, b) => a.name.localeCompare(b.name));
    tree.forEach((rootFolder, index) => {
        processFolder(rootFolder, `${index + 1} `);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Presupuesto');
    XLSX.writeFile(wb, 'Presupuesto_Export.xlsx');
};

/**
 * Parsed row interface
 */
interface BudgetRow {
    level: string; // "1", "1.1", "1.1.1"
    description: string;
    unit?: string;
    quantity?: number;
    unitPrice?: number;
    id?: string; // Added ID field
}

/**
 * Parses an imported Excel file into BudgetFolders and BudgetItems.
 * 
 * Logic:
 * 1. Read all rows.
 * 2. Sort by Level to ensure parents come before children.
 * 3. Reconstruct the tree.
 * 4. Identify Folders vs Items:
 *    - If a node is a parent of another node -> It's a Folder.
 *    - If a node has no children -> It's an Item (or empty folder). 
 *      For simplicity, if it has Unit/Quantity/Price, we treat it as an Item. Otherwise, a Folder.
 */
export const parseBudgetExcel = async (file: File): Promise<{ tree: BudgetFolder[], items: BudgetItem[] }> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        
        reader.onerror = () => {
            reject("Error al leer el archivo. Es posible que el archivo esté corrupto o sea inaccesible.");
        };

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];

                // Convert to JSON array of arrays (skipping header is manual or via option)
                const rawRows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

                if (rawRows.length < 2) {
                    reject("El archivo parece estar vacío o no tiene encabezados.");
                    return;
                }

                // Process rows
                const budgetRows: BudgetRow[] = [];
                // Skip header row (index 0)
                for (let i = 1; i < rawRows.length; i++) {
                    const row = rawRows[i];
                    if (!row[0]) continue; // Skip empty levels

                    // Ensure level is a string (Excel might treat "1.1" as number)
                    let level = String(row[0]).trim();

                    budgetRows.push({
                        level: level,
                        description: row[1] ? String(row[1]).trim() : 'Sin descripción',
                        unit: row[2] ? String(row[2]).trim() : undefined,
                        quantity: row[3] ? parseFloat(row[3]) : undefined,
                        unitPrice: row[4] ? parseFloat(row[4]) : undefined,
                        id: row[5] ? String(row[5]).trim() : undefined, // Read ID column
                    });
                }

                // Sort by Level (lexicographical sort works for 1, 1.1, 1.2 but be careful with 1.10)
                // Better sort: treat specific parts
                budgetRows.sort((a, b) => {
                    return a.level.localeCompare(b.level, undefined, { numeric: true, sensitivity: 'base' });
                });

                // Build Logic
                // We need to identify if a row is a Folder or Item.
                // A row is a FOLDER if:
                // 1. Another row starts with this row's level + "." (is a parent)
                // 2. OR it has NO unit/quantity/price (legacy behavior for folders)

                const folders: BudgetFolder[] = [];
                const items: BudgetItem[] = [];

                // Helper to generate IDs
                const generateId = () => Math.random().toString(36).substr(2, 9);

                // Map Level -> generated ID
                const levelToIdMap: Record<string, string> = {};

                // Map Level -> Created Folder Object (reference)
                const levelToFolderMap: Record<string, BudgetFolder> = {};

                // Helper: Parent detection supporting alphanumeric (Dot notation)
                // A node is a child if its level starts with "ParentLevel."
                const isChildOf = (childLvl: string, parentLvl: string) => {
                    return childLvl.startsWith(parentLvl + '.') && childLvl !== parentLvl;
                };

                // Determine if a row represents a folder
                const isRowFolder = (row: BudgetRow) => {
                    // Check if any other row is a child of this one (i.e., this row is a parent)
                    const hasChildren = budgetRows.some(r => isChildOf(r.level, row.level));
                    // Or if it lacks item-specific properties (unit, quantity, price)
                    const looksLikeFolder = !row.unit && !row.quantity && !row.unitPrice;
                    return hasChildren || looksLikeFolder;
                };

                // First pass: Populate levelToIdMap, using existing IDs or generating new ones
                budgetRows.forEach(row => {
                    levelToIdMap[row.level] = row.id || generateId();
                });

                // Second pass: Build the tree and items
                for (const row of budgetRows) {
                    const id = levelToIdMap[row.level]; // Get the ID for this level (either existing or newly generated)

                    if (isRowFolder(row)) {
                        const folder: BudgetFolder = {
                            id: id,
                            name: row.description, // + ` (${ row.level })`,
                            children: [],
                            expanded: true
                        };
                        levelToFolderMap[row.level] = folder;

                        // Attach to parent folder if exists
                        // Parent Level = 1.1.1 -> 1.1
                        const lastDotIndex = row.level.lastIndexOf('.');
                        if (lastDotIndex > -1) {
                            const parentLevel = row.level.substring(0, lastDotIndex);
                            const parentFolder = levelToFolderMap[parentLevel];
                            if (parentFolder) {
                                parentFolder.children.push(folder);
                            } else {
                                // Parent doesn't exist or wasn't processed yet (shouldn't happen with sorted data)
                                // Treat as root for safety.
                                folders.push(folder);
                            }
                        } else {
                            // No dots -> Root level
                            folders.push(folder);
                        }
                    } else {
                        // It is an Item
                        // Find parent folder ID
                        const lastDotIndex = row.level.lastIndexOf('.');
                        let parentId = 'root-or-missing'; // Fallback

                        if (lastDotIndex > -1) {
                            const parentLevel = row.level.substring(0, lastDotIndex);
                            if (levelToIdMap[parentLevel]) {
                                parentId = levelToIdMap[parentLevel];
                            }
                        } else {
                            // If an item has no dot in its level (e.g., "1" is an item), it's a root item.
                            // We don't have a specific "root" folder ID in the app, so we'll use a placeholder.
                            // These items will appear as "uncategorized" or need special handling in the UI.
                            // For now, they'll be assigned to 'root-or-missing'.
                        }

                        const item: BudgetItem = {
                            // If the ID from Excel starts with 'item-', use it directly.
                            // If it's a new ID (generated) or doesn't have 'item-', add the prefix.
                            id: row.id && row.id.startsWith('item-') ? row.id : `item - ${id} `,
                            folderId: parentId,
                            code: row.level, // Store WBS code as code
                            description: row.description,
                            unit: row.unit || '',
                            quantity: row.quantity || 0,
                            unitPrice: row.unitPrice || 0,
                            ganttTaskId: '' // Initialize ganttTaskId
                        };
                        items.push(item);
                    }
                }

                resolve({ tree: folders, items: items });

            } catch (err) {
                reject("Error al analizar el archivo: " + err);
            }
        };
        reader.readAsArrayBuffer(file);
    });
};
